import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { motion } from 'framer-motion';
import DashboardLayout from '../../components/dashboard/DashboardLayout';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Icon from '../../components/AppIcon';
import creditService from '../../utils/creditService';
import campaignServiceEnhanced from '../../utils/campaignServiceEnhanced';
import apolloService from '../../utils/apolloService';
import { supabase } from '../../utils/supabase';

function CampaignsPageEnhanced() {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [creditBalance, setCreditBalance] = useState(0);
  const [isApolloAuthenticated, setIsApolloAuthenticated] = useState(false);
  const [creatingCampaign, setCreatingCampaign] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    loadCampaigns();
    loadCreditBalance();
    checkApolloAuthentication();
  }, [user?.id]);

  /**
   * Check if user has Apollo authentication
   */
  const checkApolloAuthentication = async () => {
    try {
      const result = await apolloService.validateConnection();
      setIsApolloAuthenticated(result.success);
    } catch (err) {
      console.error('Error checking Apollo auth:', err);
      setIsApolloAuthenticated(false);
    }
  };


  const loadCampaigns = async () => {
    try {
      setLoading(true);
      const result = await campaignServiceEnhanced.getCampaigns(user?.id);

      if (result?.success) {
        setCampaigns(result.data);
      } else {
        setError(result?.error || 'Failed to load campaigns');
      }
    } catch (err) {
      console.error('Error loading campaigns:', err);
      setError('An error occurred while loading campaigns');
    } finally {
      setLoading(false);
    }
  };


  const loadCreditBalance = async () => {
    if (user?.id) {
      try {
        const result = await creditService.getCreditBalance(user.id);
        if (result?.success) {
          setCreditBalance(result.data.credits);
        }
      } catch (err) {
        console.error('Error loading credit balance:', err);
      }
    }
  };


  // Targeting and campaign creation moved to NewCampaignPage

  /**
   * Initiate Apollo OAuth authentication
   */
  const handleApolloAuth = async () => {
    try {
      setError('');

      const authResult = await apolloService.getAuthorizationUrl(user.id);
      if (!authResult.success) {
        setError(authResult.error);
        return;
      }

      // Redirect to Apollo authorization URL
      window.location.href = authResult.data.authUrl;
    } catch (err) {
      console.error('Error initiating Apollo auth:', err);
      setError('Failed to initiate Apollo authentication');
    }
  };

  // Form submission moved to NewCampaignPage

  /**
   * Filter campaigns based on search
   */
  const filteredCampaigns = campaigns.filter(campaign =>
    campaign.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    campaign.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <DashboardLayout title="Campaigns" currentPath="/campaigns">
        <div className="flex items-center justify-center h-64">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground font-body-medium">Loading campaigns...</p>
          </motion.div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Campaigns" currentPath="/campaigns">
      <div className="space-y-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between"
        >
          <div>
            <h1 className="text-3xl font-headline-bold text-foreground mb-2">Campaigns</h1>
            <p className="text-muted-foreground font-body">
              Create and manage your AI SDR campaigns
            </p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="glassmorphism rounded-lg px-4 py-2 border border-border">
              <p className="text-sm text-muted-foreground">Credits</p>
              <p className="text-2xl font-headline-bold text-warning">{creditBalance}</p>
            </div>

            {/* Apollo Connection Status Badge */}
            <div className={`glassmorphism rounded-lg px-4 py-2 border ${isApolloAuthenticated
              ? 'border-success/30 bg-success/5'
              : 'border-warning/30 bg-warning/5'
              }`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${isApolloAuthenticated ? 'bg-success' : 'bg-warning'
                  }`}></div>
                <span className={`text-xs font-body-semibold ${isApolloAuthenticated ? 'text-success' : 'text-warning'
                  }`}>
                  {isApolloAuthenticated ? 'Apollo Connected' : 'Apollo Not Connected'}
                </span>
              </div>
            </div>

            {/* Create Campaign / Connect Apollo Button */}
            <Button
              onClick={() => {
                navigate('/campaigns/new');
              }}
              className="cta-button"
              iconName="Plus"
              iconPosition="left"
            >
              New Campaign
            </Button>
          </div>
        </motion.div>

        {/* Error Messages */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glassmorphism rounded-lg p-4 border border-error/20 bg-error/10"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <p className="text-sm text-error font-body-medium">{error}</p>
              </div>
              {error.includes('Apollo') && error.includes('required') && !isApolloAuthenticated && (
                <Button
                  onClick={handleApolloAuth}
                  variant="outline"
                  size="sm"
                  className="ml-3 whitespace-nowrap"
                >
                  Authorize Now
                </Button>
              )}
            </div>
          </motion.div>
        )}

        {/* Success Messages */}
        {success && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="glassmorphism rounded-lg p-4 border border-success/20 bg-success/10"
          >
            <p className="text-sm text-success font-body-medium">{success}</p>
          </motion.div>
        )}

        {/* Search Bar */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.1 }}
        >
          <Input
            type="text"
            placeholder="Search campaigns..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            iconName="Search"
            className="w-full"
          />
        </motion.div>

        {/* Campaigns Grid */}
        {filteredCampaigns.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCampaigns.map((campaign, index) => (
              <motion.div
                key={campaign.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => navigate(`/campaigns/${campaign.id}`)}
                className="glassmorphism rounded-xl p-6 border border-border hover:border-primary cursor-pointer transition-all duration-300 hover:shadow-lg hover:-translate-y-1"
              >
                {/* Status Badge */}
                <div className="flex items-center justify-between mb-4">
                  <div className={`px-3 py-1 rounded-full text-xs font-body-semibold ${campaign.campaign_status === 'completed' ? 'bg-success/20 text-success' :
                    campaign.campaign_status === 'processing' ? 'bg-primary/20 text-primary' :
                      campaign.campaign_status === 'failed' ? 'bg-error/20 text-error' :
                        'bg-muted/20 text-muted-foreground'
                    }`}>
                    {campaign.campaign_status.charAt(0).toUpperCase() + campaign.campaign_status.slice(1)}
                  </div>
                  <Icon name="MoreVertical" size={20} color="var(--color-muted-foreground)" />
                </div>

                {/* Campaign Name */}
                <h3 className="text-lg font-headline-bold text-foreground mb-2 line-clamp-2">
                  {campaign.name}
                </h3>

                {/* Description */}
                {campaign.description && (
                  <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                    {campaign.description}
                  </p>
                )}

                {/* Stats */}
                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="bg-card/50 rounded-lg p-2 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Leads</p>
                    <p className="text-lg font-headline-bold text-foreground">
                      {campaign.total_leads || 0}
                    </p>
                  </div>
                  <div className="bg-card/50 rounded-lg p-2 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Replied</p>
                    <p className="text-lg font-headline-bold text-success">
                      {campaign.replied_count || 0}
                    </p>
                  </div>
                  <div className="bg-card/50 rounded-lg p-2 text-center">
                    <p className="text-xs text-muted-foreground mb-1">Meetings</p>
                    <p className="text-lg font-headline-bold text-primary">
                      {campaign.meetings_booked || 0}
                    </p>
                  </div>
                </div>

                {/* Targeting Info */}
                <div className="border-t border-border pt-3">
                  <p className="text-xs text-muted-foreground mb-2">Targeting:</p>
                  <div className="flex flex-wrap gap-1">
                    {campaign.target_job_titles?.slice(0, 2).map((title, i) => (
                      <span key={i} className="text-xs bg-primary/10 text-primary rounded px-2 py-1">
                        {title}
                      </span>
                    ))}
                    {campaign.target_job_titles?.length > 2 && (
                      <span className="text-xs text-muted-foreground">
                        +{campaign.target_job_titles.length - 2} more
                      </span>
                    )}
                  </div>
                </div>

                {/* Created Date */}
                <p className="text-xs text-muted-foreground mt-4">
                  Created {new Date(campaign.created_at).toLocaleDateString()}
                </p>
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="glassmorphism rounded-xl p-12 border border-dashed border-border text-center"
          >
            <Icon name="Inbox" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-4" />
            <h3 className="text-lg font-headline-bold text-foreground mb-2">No Campaigns Yet</h3>
            <p className="text-muted-foreground font-body mb-6">
              {searchTerm ? 'No campaigns match your search' : 'Create your first campaign to get started'}
            </p>
            {!searchTerm && (
              <Button
                onClick={() => navigate('/campaigns/new')}
                disabled={creditBalance < 20}
                className="cta-button"
              >
                Create Campaign
              </Button>
            )}
          </motion.div>
        )}

        {/* Create Campaign Modal moved to NewCampaignPage */}
      </div>
    </DashboardLayout>
  );
}

export default CampaignsPageEnhanced;
